import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import CropFreeIcon from '@mui/icons-material/CropFree';
import SearchIcon from '@mui/icons-material/Search';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import NearMeIcon from '@mui/icons-material/NearMe';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import seedCscCentersIfNeeded from '../../pages/CSCOwnerPortal/CSCOwnerServices/seedCscCenters';
import './ServiceCentersMap.styles.scss';

// ── Ratings helpers ───────────────────────────────────────────────────────────
const getRatingsStore = () => {
  try { return JSON.parse(localStorage.getItem('cscRatings') || '{}'); }
  catch { return {}; }
};

const getRatingForCenter = (centerId) => {
  const store = getRatingsStore();
  return store[centerId] || { avgRating: 0, count: 0, userRating: null };
};


// Seed some initial ratings for realism
const seedRatingsIfNeeded = () => {
  if (localStorage.getItem('cscRatingsSeedV1')) return;
  try {
    const centers = JSON.parse(localStorage.getItem('cscCenters') || '[]');
    const store = {};
    const BASE = [4.5, 3.8, 4.2, 4.0, 3.5, 4.7, 4.1, 3.9, 4.3, 4.6];
    centers.forEach((c, i) => {
      const id = String(c.id ?? c._id ?? `center-${i}`);
      store[id] = { avgRating: BASE[i % BASE.length], count: 8 + (i % 15), userRating: null };
    });
    localStorage.setItem('cscRatings', JSON.stringify(store));
    localStorage.setItem('cscRatingsSeedV1', 'true');
  } catch { /* ignore */ }
};

// ── CenterRatingDisplay — read-only, shown on map ────────────────────────────
const CenterRatingDisplay = ({ centerId }) => {
  const rating = getRatingForCenter(centerId);
  const displayAvg = rating.avgRating;

  const renderStar = (i) => {
    if (displayAvg >= i) return <StarIcon key={i} className="svm-star svm-star--filled" />;
    if (displayAvg >= i - 0.5) return <StarHalfIcon key={i} className="svm-star svm-star--half" />;
    return <StarBorderIcon key={i} className="svm-star svm-star--empty" />;
  };

  return (
    <div className="svm-rating">
      <div className="svm-rating__avg-row">
        <div className="svm-rating__stars-display">
          {[1, 2, 3, 4, 5].map((i) => renderStar(i))}
        </div>
        <span className="svm-rating__avg-num">{displayAvg > 0 ? displayAvg.toFixed(1) : '—'}</span>
        <span className="svm-rating__count">
          {rating.count > 0 ? `(${rating.count} review${rating.count !== 1 ? 's' : ''})` : 'No reviews yet'}
        </span>
      </div>
      <p className="svm-rating__readonly-hint">
        Complete a service at this center to leave a review.
      </p>
    </div>
  );
};

// ── City coordinate lookup ────────────────────────────────────────────────────
const CITY_COORDS = {
  hisar: [29.1517, 75.7217], rohtak: [28.8955, 76.6066],
  delhi: [28.6139, 77.2090], 'new delhi': [28.6139, 77.2090],
  mumbai: [19.0760, 72.8777], pune: [18.5204, 73.8567],
  bangalore: [12.9716, 77.5946], bengaluru: [12.9716, 77.5946],
  hyderabad: [17.3850, 78.4867], chennai: [13.0827, 80.2707],
  kolkata: [22.5726, 88.3639], ahmedabad: [23.0225, 72.5714],
  jaipur: [26.9124, 75.7873], chandigarh: [30.7333, 76.7794],
  lucknow: [26.8467, 80.9462], patna: [25.5941, 85.1376],
  bhopal: [23.2599, 77.4126], indore: [22.7196, 75.8577],
  nagpur: [21.1458, 79.0882], surat: [21.1702, 72.8311],
  visakhapatnam: [17.6868, 83.2185], kochi: [9.9312, 76.2673],
  amritsar: [31.6340, 74.8723], ludhiana: [30.9010, 75.8573],
  agra: [27.1767, 78.0081], varanasi: [25.3176, 82.9739],
  ranchi: [23.3441, 85.3096], bhubaneswar: [20.2961, 85.8245],
  guwahati: [26.1445, 91.7362], dehradun: [30.3165, 78.0322],
  raipur: [21.2514, 81.6296], coimbatore: [11.0168, 76.9558],
  madurai: [9.9252, 78.1198], noida: [28.5355, 77.3910],
  gurgaon: [28.4595, 77.0266], gurugram: [28.4595, 77.0266],
  faridabad: [28.4089, 77.3178], panipat: [29.3909, 76.9635],
  karnal: [29.6857, 76.9905], ambala: [30.3783, 76.7767],
};

const getCityCoords = (city = '') => CITY_COORDS[city.toLowerCase().trim()] || null;

const getCenterPosition = (center) => {
  const base = getCityCoords(center.city);
  const offset = () => (Math.random() - 0.5) * 0.18;
  if (base) return [base[0] + offset(), base[1] + offset()];
  return [20.5937 + (Math.random() - 0.5) * 16, 78.9629 + (Math.random() - 0.5) * 16];
};

const haversine = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const fmtDist = (km) => (km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`);

const PIN_SVG = (color, id) => `<div class="svm-pin" data-svm-id="${id}">
  <svg viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 0C7.163 0 0 7.163 0 16C0 26 16 40 16 40C16 40 32 26 32 16C32 7.163 24.837 0 16 0Z" fill="${color}"/>
    <circle cx="16" cy="15" r="7" fill="white" fill-opacity="0.9"/>
    <circle cx="16" cy="15" r="4" fill="${color}"/>
  </svg>
  <div class="svm-pin__pulse"></div>
</div>`;

const USER_DOT_HTML = `<div class="svm-user-dot">
  <div class="svm-user-dot__ring"></div>
  <div class="svm-user-dot__inner"></div>
</div>`;

const BLIPS = Array.from({ length: 14 }, (_, i) => {
  const angle = (i / 14) * 2 * Math.PI + i * 0.3;
  const r = 18 + (i % 3) * 14;
  return { x: 50 + r * Math.cos(angle), y: 50 + r * Math.sin(angle), delay: i * 0.18 };
});

const SORT_OPTIONS = [
  { value: 'default',    label: 'Default order' },
  { value: 'price-asc',  label: 'Price: Low → High' },
  { value: 'price-desc', label: 'Price: High → Low' },
  { value: 'name',       label: 'Name A → Z' },
  { value: 'distance',   label: 'Nearest first' },
];

const FitBounds = ({ positions, phase }) => {
  const map = useMap();
  const fit = () => {
    if (positions.length === 0) return;
    if (positions.length === 1) { map.setView(positions[0], 10); return; }
    map.fitBounds(L.latLngBounds(positions), { padding: [60, 60] });
  };
  useEffect(() => {
    if (phase !== 'map') return;
    const id = setTimeout(() => { map.invalidateSize(); fit(); }, 400);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, map]);
  useEffect(() => {
    fit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions, map]);
  return null;
};

const MapController = ({ mapRef, onMapMove }) => {
  const map = useMap();
  useEffect(() => {
    mapRef.current = map;
    map.on('movestart zoomstart', onMapMove);
    return () => map.off('movestart zoomstart', onMapMove);
  }, [map, mapRef, onMapMove]);
  return null;
};

// ── Main Component ────────────────────────────────────────────────────────────
const ServiceCentersMap = () => {
  const { applicationName } = useParams();
  const navigate = useNavigate();

  const [phase, setPhase]               = useState('scanning');
  const [centers, setCenters]           = useState([]);
  const [positions, setPositions]       = useState([]);
  const [selected, setSelected]         = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [userAccuracy, setUserAccuracy] = useState(null);
  const [locating, setLocating]         = useState(false);
  const [searchQuery, setSearchQuery]   = useState('');
  const [sortOrder, setSortOrder]       = useState('default');
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [hovered, setHovered]           = useState(null);
  const mapRef = useRef(null);
  const hoverTimer = useRef(null);

  const serviceName = decodeURIComponent(applicationName || '');

  const cancelHoverClose = useCallback(() => {
    if (hoverTimer.current) { clearTimeout(hoverTimer.current); hoverTimer.current = null; }
  }, []);
  const scheduleHoverClose = useCallback(() => {
    cancelHoverClose();
    hoverTimer.current = setTimeout(() => setHovered(null), 200);
  }, [cancelHoverClose]);
  const closeHoverNow = useCallback(() => { cancelHoverClose(); setHovered(null); }, [cancelHoverClose]);
  useEffect(() => () => cancelHoverClose(), [cancelHoverClose]);

  const handlePinMouseOver = useCallback((center, e) => {
    cancelHoverClose();
    const map = mapRef.current || e?.target?._map;
    if (!map) return;
    const pt = map.latLngToContainerPoint(e.target.getLatLng());
    setHovered({ center, x: pt.x, y: pt.y });
  }, [cancelHoverClose]);

  const handlePinClick = useCallback((center) => {
    cancelHoverClose();
    setHovered(null);
    setSelected(center);
  }, [cancelHoverClose]);

  const pinIcons = useMemo(() => {
    const map = {};
    centers.forEach((c) => {
      map[c._id] = L.divIcon({ className: '', html: PIN_SVG('#1A8089', c._id), iconSize: [36, 46], iconAnchor: [18, 46] });
    });
    return map;
  }, [centers]);
  const userIcon = useMemo(() => L.divIcon({ className: '', html: USER_DOT_HTML, iconSize: [20, 20], iconAnchor: [10, 10] }), []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.getContainer().querySelectorAll('.svm-pin').forEach((pin) => {
      pin.classList.toggle('svm-pin--active', pin.getAttribute('data-svm-id') === String(selected?._id));
    });
  }, [selected, centers, phase]);

  useEffect(() => {
    seedCscCentersIfNeeded();
    seedRatingsIfNeeded();
    try {
      const allCenters  = JSON.parse(localStorage.getItem('cscCenters')  || '[]');
      const allServices = JSON.parse(localStorage.getItem('cscServices') || '[]');

      // Find the service definition — tells us which state/scope it belongs to
      const svcDef = allServices.find((s) => s.serviceName === serviceName);

      let filtered = [];

      if (svcDef) {
        const svcState = svcDef.state || 'All India';
        // All India → every center; state-specific → centers in that state
        filtered = allCenters
          .filter((c) => svcState === 'All India' || c.state === svcState)
          .map((c, i) => ({
            ...c,
            _id:    c.id ?? c._id ?? `center-${i}`,
            charge: svcDef.cscCharge ?? 0,
            _pos:   getCenterPosition(c),
          }));
      }

      // Fallback: legacy serviceCharges lookup (covers old mock data keys)
      if (filtered.length === 0) {
        filtered = allCenters
          .filter((c) => c.serviceCharges && Object.prototype.hasOwnProperty.call(c.serviceCharges, serviceName))
          .map((c, i) => ({
            ...c,
            _id:    c.id ?? c._id ?? `center-${i}`,
            charge: c.serviceCharges[serviceName],
            _pos:   getCenterPosition(c),
          }));
      }

      setCenters(filtered);
      setPositions(filtered.map((c) => c._pos));
    } catch { /* nothing */ }

    const t1 = setTimeout(() => setPhase('fading'), 2800);
    const t2 = setTimeout(() => setPhase('map'), 3600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [serviceName]);

  const displayedCenters = useMemo(() => {
    let result = [...centers];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((c) =>
        c.centerName?.toLowerCase().includes(q) ||
        c.city?.toLowerCase().includes(q) ||
        c.ownerName?.toLowerCase().includes(q)
      );
    }
    switch (sortOrder) {
      case 'price-asc':  result.sort((a, b) => (a.charge || 0) - (b.charge || 0)); break;
      case 'price-desc': result.sort((a, b) => (b.charge || 0) - (a.charge || 0)); break;
      case 'name':       result.sort((a, b) => (a.centerName || '').localeCompare(b.centerName || '')); break;
      case 'distance':
        if (userLocation) {
          result.sort((a, b) => {
            const dA = haversine(userLocation[0], userLocation[1], a._pos[0], a._pos[1]);
            const dB = haversine(userLocation[0], userLocation[1], b._pos[0], b._pos[1]);
            return dA - dB;
          });
        }
        break;
      default: break;
    }
    return result;
  }, [centers, searchQuery, sortOrder, userLocation]);

  const handleListItemClick = (center) => {
    setSelected(center);
    mapRef.current?.flyTo(center._pos, 13, { duration: 1 });
  };

  const handleLocate = () => {
    if (!navigator.geolocation) return;
    if (userLocation) { mapRef.current?.flyTo(userLocation, 12, { duration: 1 }); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = [pos.coords.latitude, pos.coords.longitude];
        setUserLocation(loc);
        setUserAccuracy(pos.coords.accuracy);
        mapRef.current?.flyTo(loc, 10, { duration: 1.5 });
        setSortOrder('distance');
        setLocating(false);
      },
      () => setLocating(false),
      { timeout: 10000 }
    );
  };

  const handleFitAll = () => {
    if (!mapRef.current || positions.length === 0) return;
    if (positions.length === 1) { mapRef.current.flyTo(positions[0], 12, { duration: 1 }); return; }
    mapRef.current.flyToBounds(L.latLngBounds(positions), { padding: [80, 80], duration: 1 });
  };

  const handleApply = (center) => navigate('../place-order', { state: { center, serviceName } });

  const getDist = (center) =>
    userLocation ? haversine(userLocation[0], userLocation[1], center._pos[0], center._pos[1]) : null;

  const currentSortLabel = SORT_OPTIONS.find((o) => o.value === sortOrder)?.label || 'Sort';

  return (
    <div className="svm-root">

      {/* ── LEFT: map stage ──────────────────────────────────────────────────── */}
      <div className="svm-stage">

        {/* Radar overlay */}
        {phase !== 'map' && (
          <div className={`svm-radar${phase === 'fading' ? ' svm-radar--fading' : ''}`}>
            <p className="svm-radar__title">Locating CSC Centers</p>
            <div className="svm-radar__circle">
              <div className="svm-radar__sweep" />
              <div className="svm-radar__line" />
              <div className="svm-radar__crosshair" />
              {BLIPS.map((b, i) => (
                <div key={i} className="svm-radar__blip"
                  style={{ left: `${b.x}%`, top: `${b.y}%`, animationDelay: `${b.delay}s` }} />
              ))}
            </div>
            <p className="svm-radar__sub">
              {centers.length > 0 ? `${centers.length} center${centers.length > 1 ? 's' : ''} found` : 'Scanning…'}
            </p>
          </div>
        )}

        {/* Map wrapper — MapContainer is always mounted to avoid React/Leaflet DOM conflict */}
        <div className={`svm-map-wrapper${phase === 'map' ? ' svm-map-wrapper--visible' : ''}`}>

          {/* MapContainer fills the wrapper directly — absolute positioning gives Leaflet a pixel size */}
          <MapContainer
            center={[22, 79]}
            zoom={5}
            className="svm-map"
            zoomControl={false}
            style={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              visibility: centers.length === 0 && phase === 'map' ? 'hidden' : 'visible',
            }}
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://carto.com/">CARTO</a>'
            />
            <FitBounds positions={positions} phase={phase} />
            <MapController mapRef={mapRef} onMapMove={closeHoverNow} />
            {centers.map((c) => (
              <Marker
                key={c._id}
                position={c._pos}
                icon={pinIcons[c._id]}
                eventHandlers={{
                  click:     () => handlePinClick(c),
                  mouseover: (e) => handlePinMouseOver(c, e),
                  mouseout:  () => scheduleHoverClose(),
                }}
              />
            ))}
            {userLocation && (
              <>
                <Marker position={userLocation} icon={userIcon} />
                {userAccuracy && (
                  <Circle
                    center={userLocation}
                    radius={userAccuracy}
                    pathOptions={{ color: '#4dabf7', fillColor: '#4dabf7', fillOpacity: 0.08, weight: 1.5, dashArray: '6 4' }}
                  />
                )}
              </>
            )}
          </MapContainer>

          {/* Empty state — overlaid on top, map is visibility:hidden behind it */}
          {centers.length === 0 && phase === 'map' && (
            <div className="svm-empty" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10 }}>
              <StorefrontOutlinedIcon className="svm-empty__icon" />
              <p className="svm-empty__title">No CSC centers found</p>
              <p className="svm-empty__sub">No centers currently offer this service.</p>
            </div>
          )}

          {/* Hover card */}
          {hovered && (
            <div
              className="svm-hovercard"
              style={{ left: hovered.x, top: hovered.y }}
              onMouseEnter={cancelHoverClose}
              onMouseLeave={scheduleHoverClose}
              onClick={() => handlePinClick(hovered.center)}
              role="button"
              tabIndex={0}
            >
              <div className="svm-hovercard__card">
                <div className="svm-hovercard__top">
                  <div className="svm-hovercard__avatar">
                    {hovered.center.centerName?.charAt(0).toUpperCase()}
                  </div>
                  <div className="svm-hovercard__head">
                    <p className="svm-hovercard__name">{hovered.center.centerName}</p>
                    <p className="svm-hovercard__loc">
                      <LocationOnOutlinedIcon style={{ fontSize: '0.7rem', marginRight: 2 }} />
                      {hovered.center.city}{hovered.center.state ? `, ${hovered.center.state}` : ''}
                    </p>
                  </div>
                </div>
                <div className="svm-hovercard__foot">
                  <span className={`svm-hovercard__charge${hovered.center.charge === 0 ? ' svm-hovercard__charge--free' : ''}`}>
                    {hovered.center.charge === 0 ? 'FREE' : `₹${hovered.center.charge}`}
                  </span>
                  <span className="svm-hovercard__cta">
                    View details <ArrowForwardIcon style={{ fontSize: '0.8rem' }} />
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Map header (top-left) */}
          <div className="svm-header">
            <StorefrontOutlinedIcon className="svm-header__icon" />
            <div>
              <p className="svm-header__title">{serviceName}</p>
              <p className="svm-header__sub">
                {searchQuery ? `${displayedCenters.length} of ${centers.length}` : centers.length}
                {' '}center{centers.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {/* Map controls (top-right) */}
          <div className="svm-controls">
            <button className="svm-ctrl-btn" onClick={() => mapRef.current?.zoomIn()} title="Zoom in">
              <AddIcon fontSize="small" />
            </button>
            <div className="svm-ctrl-sep" />
            <button className="svm-ctrl-btn" onClick={() => mapRef.current?.zoomOut()} title="Zoom out">
              <RemoveIcon fontSize="small" />
            </button>
            <div className="svm-ctrl-sep" />
            <button className="svm-ctrl-btn" onClick={handleFitAll} title="Fit all centers">
              <CropFreeIcon fontSize="small" />
            </button>
            <div className="svm-ctrl-sep" />
            <button
              className={`svm-ctrl-btn${locating ? ' svm-ctrl-btn--locating' : ''}${userLocation ? ' svm-ctrl-btn--located' : ''}`}
              onClick={handleLocate}
              title={userLocation ? 'Go to my location' : 'Find my location'}
            >
              <MyLocationIcon fontSize="small" />
            </button>
          </div>

          {/* Hint pill */}
          <div className="svm-hint">
            <LocationOnOutlinedIcon style={{ fontSize: '0.9rem' }} />
            Click a pin or select from the list →
          </div>
        </div>
      </div>

      {/* ── RIGHT: center list / detail panel ───────────────────────────────── */}
      <div className="svm-detail">

        {/* No service found */}
        {centers.length === 0 && phase === 'map' && (
          <div className="svm-detail__empty">
            <div className="svm-detail__empty-icon"><StorefrontOutlinedIcon /></div>
            <p className="svm-detail__empty-title">No centers available</p>
            <p className="svm-detail__empty-sub">No CSC centers currently offer this service. Check back later.</p>
          </div>
        )}

        {/* Loading placeholder */}
        {phase !== 'map' && (
          <div className="svm-detail__empty">
            <div className="svm-detail__empty-icon"><StorefrontOutlinedIcon /></div>
            <p className="svm-detail__empty-title">Locating centers…</p>
            <p className="svm-detail__empty-sub">Finding CSC centers near you.</p>
          </div>
        )}

        {/* Detail view */}
        {selected && phase === 'map' && (
          <div className="svm-panel">
            <button className="svm-panel__back" onClick={() => setSelected(null)}>
              <ArrowBackIosNewIcon style={{ fontSize: '0.85rem' }} />
              All centers
            </button>

            <div className="svm-panel__banner">
              <div className="svm-panel__avatar">{selected.centerName.charAt(0).toUpperCase()}</div>
              <div className="svm-panel__banner-info">
                <h3 className="svm-panel__name">{selected.centerName}</h3>
                <span className="svm-panel__owner">
                  <VerifiedIcon style={{ fontSize: '0.9rem', color: '#1A8089', marginRight: 4 }} />
                  {selected.ownerName}
                </span>
                {getDist(selected) !== null && (
                  <span className="svm-panel__dist">
                    <NearMeIcon style={{ fontSize: '0.8rem', marginRight: 3 }} />
                    {fmtDist(getDist(selected))} away
                  </span>
                )}
              </div>
            </div>

            <div className="svm-panel__body">
              <div className="svm-panel__row">
                <LocationOnOutlinedIcon className="svm-panel__icon" />
                <span>{selected.address}, {selected.city}, {selected.state} – {selected.pincode}</span>
              </div>
              <div className="svm-panel__row">
                <PhoneOutlinedIcon className="svm-panel__icon" />
                <a href={`tel:${selected.mobile}`}>{selected.mobile}</a>
              </div>
              <div className="svm-panel__row">
                <EmailOutlinedIcon className="svm-panel__icon" />
                <a href={`mailto:${selected.email}`}>{selected.email}</a>
              </div>
              <div className="svm-panel__charge">
                <LocalOfferOutlinedIcon className="svm-panel__charge-icon" />
                <div>
                  <span className="svm-panel__charge-label">Service Charge</span>
                  {selected.charge === 0
                    ? <span className="svm-panel__charge-free">FREE</span>
                    : <span className="svm-panel__charge-value">₹{selected.charge}</span>
                  }
                </div>
              </div>
            </div>

            <CenterRatingDisplay centerId={String(selected._id)} />

            <button className="svm-panel__apply" onClick={() => handleApply(selected)}>
              Apply Here <ArrowForwardIcon className="svm-panel__apply-icon" fontSize="small" />
            </button>
          </div>
        )}

        {/* List view */}
        {!selected && centers.length > 0 && phase === 'map' && (
          <div className="svm-panel-list">
            {/* Header */}
            <div className="svm-panel-list__header">
              <StorefrontOutlinedIcon className="svm-panel-list__header-icon" />
              <div>
                <p className="svm-panel-list__header-title">{serviceName}</p>
                <p className="svm-panel-list__header-sub">
                  {displayedCenters.length} center{displayedCenters.length !== 1 ? 's' : ''} available
                  {userLocation ? ' · sorted by distance' : ''}
                </p>
              </div>
            </div>

            {/* Search */}
            <div className="svm-panel-list__search-row">
              <SearchIcon className="svm-panel-list__search-icon" fontSize="small" />
              <input
                className="svm-panel-list__search-input"
                placeholder="Search centers, cities…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="svm-panel-list__search-clear" onClick={() => setSearchQuery('')}>
                  <CloseIcon style={{ fontSize: '0.85rem' }} />
                </button>
              )}
            </div>

            {/* Sort */}
            <div className="svm-panel-list__sort-row"
              onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setSortMenuOpen(false); }}
              tabIndex={-1}
            >
              <button
                className={`svm-panel-list__sort-btn${sortMenuOpen ? ' svm-panel-list__sort-btn--open' : ''}`}
                onClick={() => setSortMenuOpen((p) => !p)}
              >
                <SwapVertIcon style={{ fontSize: '1rem' }} />
                <span>{currentSortLabel}</span>
              </button>
              {sortMenuOpen && (
                <div className="svm-panel-list__sort-menu">
                  {SORT_OPTIONS.map((opt) => {
                    const disabled = opt.value === 'distance' && !userLocation;
                    return (
                      <button
                        key={opt.value}
                        className={`svm-panel-list__sort-option${sortOrder === opt.value ? ' svm-panel-list__sort-option--active' : ''}${disabled ? ' svm-panel-list__sort-option--disabled' : ''}`}
                        onClick={() => { if (!disabled) { setSortOrder(opt.value); setSortMenuOpen(false); } }}
                      >
                        <span>{opt.label}</span>
                        {disabled && <span className="svm-panel-list__sort-hint">locate first</span>}
                        {sortOrder === opt.value && !disabled && <CheckIcon style={{ fontSize: '0.9rem', marginLeft: 'auto' }} />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Items */}
            <div className="svm-panel-list__items">
              {displayedCenters.length === 0 ? (
                <div className="svm-panel-list__empty">No centers match your search</div>
              ) : (
                displayedCenters.map((c) => {
                  const dist = getDist(c);
                  return (
                    <button
                      key={c._id}
                      className="svm-panel-list__item"
                      onClick={() => handleListItemClick(c)}
                    >
                      <div className="svm-panel-list__item-avatar">{c.centerName?.charAt(0).toUpperCase()}</div>
                      <div className="svm-panel-list__item-body">
                        <p className="svm-panel-list__item-name">{c.centerName}</p>
                        <p className="svm-panel-list__item-city">
                          <LocationOnOutlinedIcon style={{ fontSize: '0.7rem', marginRight: 2 }} />
                          {c.city}{c.state ? `, ${c.state}` : ''}
                        </p>
                      </div>
                      <div className="svm-panel-list__item-meta">
                        <span className={`svm-panel-list__item-price${c.charge === 0 ? ' svm-panel-list__item-price--free' : ''}`}>
                          {c.charge === 0 ? 'FREE' : `₹${c.charge}`}
                        </span>
                        {dist !== null && (
                          <span className="svm-panel-list__item-dist">
                            <NearMeIcon style={{ fontSize: '0.65rem' }} />
                            {fmtDist(dist)}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceCentersMap;
